import { Component, Inject, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { CommonService } from '../../services/common.service';
import { ReportsService } from '../../services/reports.service';
import { MatSidenav } from '@angular/material/sidenav';
import { UntypedFormGroup, UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { catchError, map } from 'rxjs/operators';
import { Observable, throwError } from 'rxjs';
import { dateAfterDays, dateWithinDaysWindow, isToday } from '../../helpers/date-comparisons';
import { Config } from '../../config';

@Component({
  selector: 'vrm-order-mvr',
  templateUrl: './vrm-order-mvr.component.html',
  styleUrls: ['./vrm-order-mvr.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class VrmOrderMvrComponent implements OnInit {
  private orderMvrTranslations: { requestFailed: any; requestSent: any };

  public isOrderMvrButtonActive: boolean = true;
  public showDupExpiryError: boolean = false;
  public hasPermissionToOverride: boolean = false;
  public hideForm: boolean = false;
  public showStateOptions: boolean = false;
  public hasLicenceNumber: boolean = false;
  public orderMvrRequestFailed: boolean = false;
  public hasOrderFromCurrentDay: boolean = false;
  public earliestNextOrderDate: string;

  public mvrOrderForm: UntypedFormGroup;
  public mvrOrderEmail: UntypedFormControl;
  public mvrOrderEmailConfirmation: UntypedFormControl;
  public mvrOrderOption: UntypedFormControl;
  public mvrOverride: UntypedFormControl;

  @ViewChild('sidenav', { static: false }) sidenav: MatSidenav;

  constructor(
    public cService: CommonService,
    public rptService: ReportsService,
    private mvrOrderFormBuilder: UntypedFormBuilder,
    public dialog: MatDialogRef<VrmOrderMvrComponent>,
    @Inject(MAT_DIALOG_DATA) public dialogData: any
  ) {
    this.loadTranslationsNeeded();
    this.configurePop();
  }

  private loadTranslationsNeeded(): void {
    this.orderMvrTranslations = {
      requestSent: this.cService.getSingleTranslation('MESSAGESANDWARNINGS.mvrOrdered', 'MVR ORDER REQUEST SENT'),
      requestFailed: this.cService.getSingleTranslation(
        'MESSAGESANDWARNINGS.failedToProcessRequest',
        'Failed to process request'
      ),
    };
  }

  private loadForm(): void {
    this.mvrOrderForm = this.mvrOrderFormBuilder.group({
      emailConfirmation: this.mvrOrderFormBuilder.control(false),
      email: this.mvrOrderFormBuilder.control(null),
      mvrOrderOption: this.mvrOrderFormBuilder.control(this.getDefaultMvrOrderOption(this.dialogData.mvrOrderOptions)),
      mvrOverride: this.mvrOrderFormBuilder.control(false),
    });
  }

  private configurePop(): void {
    this.loadForm();
    this.showDupExpiryError = this.driverHasOrderWithinExpiryTime();
    this.hasPermissionToOverride = this.cService.userProfile.userPermissions?.permMvrDupOverride === 'override';
    this.showStateOptions = this.dialogData?.mvrOrderOptions?.length > 1;
    this.hasLicenceNumber = this.dialogData?.mvrLicenseNumber;
    this.hasOrderFromCurrentDay = this.driverHasOrderFromCurrentDate();
    this.earliestNextOrderDate = this.hasOrderFromCurrentDay ? this.getEarliestNextOrderDate() : null;
    this.hideForm = this.showDupExpiryError && (!this.hasPermissionToOverride || this.hasOrderFromCurrentDay);
  }

  private driverHasOrderFromCurrentDate(): boolean {
    return this.dialogData?.mvrOrderDate ? isToday(this.dialogData?.mvrOrderDate) : false;
  }

  private getEarliestNextOrderDate(): string {
    return dateAfterDays(this.dialogData?.mvrOrderDate, 1, Config.momentDateFormatUs);
  }

  private getDefaultMvrOrderOption(options: Array<any>): string {
    if (options && options.length) {
      const defaultOption = options.filter((option) => {
        return option.isDefault;
      });

      // If there's no default option, we mark the first option
      return defaultOption.length === 1 ? defaultOption[0].code : options[0].code;
    }

    return null;
  }

  private driverHasOrderWithinExpiryTime(): boolean {
    if (this.cService.userProfile.companySettings.mvrDupExpiryDays) {
      return dateWithinDaysWindow(
        this.dialogData.mvrOrderDate,
        this.cService.userProfile.companySettings.mvrDupExpiryDays
      );
    }

    return false;
  }

  public ngOnInit(): void {
    this.dialog.addPanelClass('vrm-order-popup');
    this.mvrOrderEmail = this.mvrOrderForm.get('email') as UntypedFormControl;
    this.mvrOrderEmailConfirmation = this.mvrOrderForm.get('emailConfirmation') as UntypedFormControl;
    this.mvrOrderOption = this.mvrOrderForm.get('mvrOrderOption') as UntypedFormControl;
    this.mvrOverride = this.mvrOrderForm.get('mvrOverride') as UntypedFormControl;

    this.configureEmailValidators();
  }

  private configureEmailValidators(): void {
    this.mvrOrderEmail.setValidators([
      Validators.required,
      Validators.minLength(6),
      Validators.maxLength(50),
      Validators.email,
      Validators.pattern('^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,4}$'),
    ]);

    // As the default option is to not send alets, disable the field until the change that option
    this.mvrOrderEmail.disable();

    this.mvrOrderEmailConfirmation.valueChanges.subscribe((value) => {
      if (value) {
        this.mvrOrderEmail.enable();
      } else {
        this.mvrOrderEmail.disable();
      }
      this.mvrOrderEmail.updateValueAndValidity();
    });
  }

  public submitMvrOrder(): void {
    const formData = Object.assign({}, this.mvrOrderForm.value);
    const email = formData.emailConfirmation ? formData.email : null;
    const stateOption = formData.mvrOrderOption ? formData.mvrOrderOption : null;
    const override = formData.mvrOverride ? formData.mvrOverride : null;

    // Disable button while request is performed
    this.deactivateOrderMvrButton();

    this.sendMvrOrderRequest(email, stateOption, override).subscribe({
      error: (err: any) => {
        this.activateOrderMvrButton();
      },
    });
  }

  private sendMvrOrderRequest(email: string, stateOption: string, override?: number): Observable<any> {
    return this.cService
      .commonPost('mvr/order', {
        pin: this.dialogData.pin,
        email: email,
        stateOption: stateOption,
        override: override,
      })
      .pipe(
        map((response) => {
          console.log(response);

          this.showRequestOutcome(this.orderMvrTranslations.requestSent);
          this.dialog.close();

          return response;
        }),
        catchError((err) => {
          console.log(err);

          this.showRequestOutcome(this.orderMvrTranslations.requestFailed);
          this.orderMvrRequestFailed = true;

          return throwError(err);
        })
      );
  }

  private deactivateOrderMvrButton(): void {
    this.isOrderMvrButtonActive = false;
  }

  private activateOrderMvrButton(): void {
    this.isOrderMvrButtonActive = true;
  }

  private showRequestOutcome(message: string): void {
    this.cService.showToast(message, 3000);
  }
}
